import React from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  ListGroup,
  ListGroupItem,
  Progress
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import EducationDashboard from "../../education";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import { EDU_ACTIVITY_ON, EDU_ACTIVITY_OFF } from "Constants/reactifyInlineStyles";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import { AsyncStateBoundary, SuitePageShell } from "../../_suite/primitives";

function CoursesOutlinePage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.education.listCourses(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.edu.courses" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {(data || []).map(c => (
            <Col md="6" key={c.id} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <CardBody>
                  <Row>
                    <Col xs="4" className="border-end">
                      <ListGroup flush className="small">
                        {(c.modules || []).map((mod, i) => (
                          <ListGroupItem
                            key={i}
                            className={`px-2 py-1 ${i === 0 ? "bg-light" : ""}`}
                          >
                            {mod.name}{" "}
                            <span className="text-muted">({mod.pct}%)</span>
                          </ListGroupItem>
                        ))}
                        {(!c.modules || c.modules.length === 0) && (
                          <ListGroupItem className="px-2 py-1 text-muted">
                            Wire LMS outline
                          </ListGroupItem>
                        )}
                      </ListGroup>
                    </Col>
                    <Col xs="8">
                      <h5>{c.title}</h5>
                      <p className="small text-muted">
                        {c.learners} learners · {c.completionPct}% completion
                      </p>
                      <Progress value={c.completionPct} className="mb-2" />
                      <Button tag={Link} color="primary" size="sm" to={c.id} relative="path">
                        Course shell
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function CourseDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.education.getCourse(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.edu.course-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Card>
            <CardBody>
              <h3>{data.title}</h3>
              <p>
                Learners {data.learners} · Completion {data.completionPct}%
              </p>
            </CardBody>
          </Card>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function CohortProgressPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.education.listCohorts(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.edu.cohorts" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(c => (
          <Card key={c.id} className="mb-3 border-0 shadow-sm">
            <CardBody>
              <div className="d-flex justify-content-between mb-2">
                <strong>{c.name}</strong>
                <span>{c.learners} learners</span>
              </div>
              <div className="small text-muted mb-1">Active {c.activePct}%</div>
              <Progress value={c.activePct} color="success" />
              {c.atRisk != null && c.atRisk > 0 && (
                <p className="small text-warning mb-0 mt-2">
                  {c.atRisk} learners flagged at-risk (demo)
                </p>
              )}
              <Button tag={Link} color="link" size="sm" className="px-0 mt-2" to={c.id} relative="path">
                Cohort detail →
              </Button>
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function CohortDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.education.getCohort(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.edu.cohort-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Card>
            <CardBody>
              <h4>{data.name}</h4>
              <p>Learners {data.learners}</p>
              {data.atRisk != null && (
                <p className="small text-muted">
                  At-risk cohort share: {data.atRisk}%
                </p>
              )}
            </CardBody>
          </Card>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function LearnersActivityPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.education.listLearners(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.edu.learners" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(l => (
          <Card key={l.id} className="mb-3 border-0 shadow-sm">
            <CardBody>
              <div className="d-flex justify-content-between">
                <div>
                  <strong>{l.name}</strong>
                  <div className="small text-muted">{l.email}</div>
                </div>
                <div className="text-end">
                  <div className="small">Activity (7d)</div>
                  {l.lastActive && (
                    <div className="small text-muted">Seen {l.lastActive}</div>
                  )}
                  <div className="d-flex gap-1 justify-content-end mt-1">
                    {[1, 1, 0, 1, 1, 1, 0].map((on, i) => (
                      <span
                        key={i}
                        className="rounded"
                        style={on ? EDU_ACTIVITY_ON : EDU_ACTIVITY_OFF}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <Button tag={Link} color="link" size="sm" className="px-0" to={l.id} relative="path">
                Profile
              </Button>
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function LearnerDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.education.getLearner(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.edu.learner-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Card>
            <CardBody>
              <h4>{data.name}</h4>
              <p>{data.email}</p>
              <p>Active courses: {data.coursesActive}</p>
            </CardBody>
          </Card>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

export function EducationSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="education" />}>
        <Route index element={<EducationDashboard />} />
        <Route path="courses" element={<CoursesOutlinePage />} />
        <Route path="courses/:id" element={<CourseDetailPage />} />
        <Route path="cohorts" element={<CohortProgressPage />} />
        <Route path="cohorts/:id" element={<CohortDetailPage />} />
        <Route path="learners" element={<LearnersActivityPage />} />
        <Route path="learners/:id" element={<LearnerDetailPage />} />
      </Route>
    </Routes>
  );
}
